import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import splash from '@/assets/login-splash.jpg';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { scrimFor, type Scrim } from '@/loginTone';
import type { LoginPicture } from '@/stores/company';

/** Matches the stylesheet: at or below 640px the panel is hidden. */
export const WIDE_ENOUGH = '(min-width: 641px)';
/** How long to wait for the company's picture before showing the built-in one. */
export const PENDING_TIMEOUT_MS = 1500;

/**
 * - a LoginPicture: the MD's picture
 * - null: none chosen, so the built-in photograph
 * - 'pending': not known yet (first visit on this machine, server not answered)
 */
export type BrandPicture = LoginPicture | null | 'pending';

/**
 * The picture side of the sign-in screen. Also drawn in Settings › Company as
 * the preview, so what the MD sees there is exactly what everyone gets.
 *
 * Whatever the picture, the text stays readable: the shade over it is worked
 * out from how bright it is (loginTone.ts). While it loads, its own colours
 * show; if it cannot be loaded at all, the built-in photograph takes its
 * place rather than leaving an empty panel.
 */
export function LoginBrand({
  picture,
  name,
  logoDataUrl,
  src,
  preview = false,
}: {
  picture: BrandPicture;
  /** The company's own name, or null for the product's. */
  name: string | null;
  logoDataUrl: string | null;
  /** A picture not saved yet (Settings preview): shown instead of `picture.url`. */
  src?: string;
  /** Settings preview: shown at any window width, and never waits. */
  preview?: boolean;
}) {
  const wide = useMediaQuery(WIDE_ENOUGH);
  const [gaveUp, setGaveUp] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (picture !== 'pending') return;
    const timer = window.setTimeout(() => setGaveUp(true), PENDING_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [picture]);

  const custom = picture !== null && picture !== 'pending' ? picture : null;
  const customSrc = src ?? custom?.url ?? null;
  // A picture that would not load falls back to the built-in one; a different
  // picture (a new upload) gets its own try.
  const useCustom = customSrc !== null && failed !== customSrc;
  const waiting = picture === 'pending' && !gaveUp && !src;

  // Where the text actually is, so the shade worked out for it covers all
  // of it: a long name over two lines, a tall logo, Urdu, a larger font.
  const panel = useRef<HTMLElement>(null);
  const topText = useRef<HTMLDivElement>(null);
  const footText = useRef<HTMLDivElement>(null);
  const [textAt, setTextAt] = useState<{ topEnd: number; bottomStart: number } | null>(null);
  useLayoutEffect(() => {
    const el = panel.current;
    if (!el || typeof ResizeObserver !== 'function') return;
    const measure = () => {
      const box = el.getBoundingClientRect();
      const top = topText.current?.getBoundingClientRect();
      const foot = footText.current?.getBoundingClientRect();
      if (!top || !foot || box.height === 0) return;
      // A little beyond the text, for its glow and descenders.
      const topEnd = Math.round(top.bottom - box.top + 12);
      const bottomStart = Math.round(foot.top - box.top - 12);
      setTextAt((prev) =>
        prev?.topEnd === topEnd && prev.bottomStart === bottomStart
          ? prev
          : { topEnd, bottomStart: Math.max(topEnd, bottomStart) },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // The logo arriving, or the name wrapping, moves the text without
    // changing the panel's own size.
    if (topText.current) observer.observe(topText.current);
    if (footText.current) observer.observe(footText.current);
    return () => observer.disconnect();
  }, []);

  const scrim: Scrim = useCustom ? scrimFor(custom) : scrimFor(null);
  const style = {
    '--login-tint': scrim.tint,
    '--login-scrim-top': scrim.top,
    '--login-scrim-mid': scrim.mid,
    '--login-scrim-bottom': scrim.bottom,
    ...(useCustom && custom ? { '--login-placeholder': placeholder(custom.palette) } : {}),
    ...(textAt
      ? { '--login-top-end': `${textAt.topEnd}px`, '--login-bottom-start': `${textAt.bottomStart}px` }
      : {}),
  } as CSSProperties;

  // On a phone the panel is hidden; not rendering the picture at all keeps a
  // phone from downloading it. (An <img> inside display:none still loads.)
  const showPicture = !waiting && (wide || preview);

  return (
    <aside
      ref={panel}
      className={`login__brand${preview ? ' login__brand--preview' : ''}`}
      style={style}
      data-picture={waiting ? 'pending' : useCustom ? 'custom' : 'built-in'}
    >
      {showPicture && (
        <BrandImage
          key={useCustom ? customSrc : splash}
          src={useCustom ? customSrc : splash}
          // The built-in photograph is portrait and was framed on the flock in
          // its lower part; anyone's own picture is kept centred.
          position={useCustom ? 'center' : 'center 70%'}
          onFail={() => {
            if (useCustom) setFailed(customSrc);
          }}
        />
      )}
      <div ref={topText}>
        {logoDataUrl && <img src={logoDataUrl} alt="" className="login__logo" />}
        <div className="login__wordmark">{name ?? 'Poultry MS'}</div>
        <div className="login__tagline">
          {name ? 'Poultry MS · Inventory · Operations · Finance' : 'Inventory · Operations · Finance'}
        </div>
      </div>

      <div className="login__brandFoot" ref={footText}>
        <p>
          What you see after signing in — farms, costs, approvals — depends on the roles your
          administrator gave you.
        </p>
      </div>
    </aside>
  );
}

/** Fades in once decoded, so a slow connection never shows it painting row by row. */
function BrandImage({ src, position, onFail }: { src: string; position: string; onFail: () => void }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      decoding="async"
      draggable={false}
      className="login__brandImg"
      data-loaded={loaded ? 'true' : 'false'}
      style={{ objectPosition: position }}
      // Already in the browser's cache: complete before React could listen.
      ref={(img) => {
        if (img?.complete && img.naturalWidth > 0) setLoaded(true);
      }}
      onLoad={() => setLoaded(true)}
      onError={onFail}
    />
  );
}

/** The picture's own top, middle and bottom colours, shown while it loads. */
function placeholder(palette: string): string | undefined {
  const colours = palette.split(',');
  if (colours.length !== 3 || !colours.every((c) => /^#[0-9a-f]{6}$/i.test(c))) return undefined;
  return `linear-gradient(180deg, ${colours[0]} 0%, ${colours[1]} 50%, ${colours[2]} 100%)`;
}
